import Button from 'components/Button';
import Icon from 'components/Icon';
import Link from 'components/Link';
import {FC, HTMLAttributes, ReactNode, useState} from 'react';
import {twMerge} from 'tailwind-merge';
import {c} from 'ttag';

export type AccordionProps = {
    children?: ReactNode;
    className?: string;
    hasArrow?: boolean;
    hasBorderWhenExpanded?: boolean;
    header: ReactNode;
    hideDividerIfLast?: boolean;
    isOpenByDefault?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const Accordion: FC<AccordionProps> = ({
    children,
    className,
    hasArrow = true,
    hasBorderWhenExpanded = true,
    header,
    hideDividerIfLast,
    isOpenByDefault = false,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(isOpenByDefault);

    const handleClick = () => {
        setIsOpen((currentState) => !currentState);
    };

    return (
        <div
            className={twMerge(
                'border-b border-grey-400',
                isOpen && !hasBorderWhenExpanded && 'border-0',
                hideDividerIfLast && 'last:border-transparent',
                className,
            )}
            role="region"
            {...rest}
        >
            <Button
                className={twMerge(
                    !children && 'hover:cursor-default',
                    'flex w-full justify-between border-none pl-0 pt-1.5 pb-2 h-fit',
                    'focus-visible:border-blue-400 focus-visible:outline-offset-2 focus-visible:outline-blue-200',
                )}
                kind="custom"
                onClick={handleClick}
                size="sm"
            >
                <span className="overflow-hidden text-ellipsis whitespace-normal text-left text-sm font-semibold text-light-primary">
                    {header}
                </span>
                {children &&
                    (hasArrow ? (
                        <Icon
                            aria-label="arrow"
                            className="pl-2 pt-0.5"
                            icon={isOpen ? 'mid_arrow_up' : 'mid_arrow_down'}
                            size="base"
                        />
                    ) : (
                        <Link className="flex items-start">
                            {isOpen
                                ? c('component').t`Hide`
                                : c('component').t`Show`}
                        </Link>
                    ))}
            </Button>
            {isOpen && children && (
                <div
                    className={twMerge(
                        hasBorderWhenExpanded && 'pb-4',
                        'text-sm text-light-secondary',
                    )}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export default Accordion;
